import { Ban } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { BudgetProgress } from './BudgetProgress'
import type { BudgetExceededDetails } from '@/types/budget'

export interface BudgetExhaustedModalProps {
  /** Whether the modal is open */
  open: boolean
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void
  /** Budget exceeded details from HTTP 402 response */
  details?: BudgetExceededDetails
}

/**
 * Modal shown when HTTP 402 (Budget Exceeded) is returned.
 * Displays usage info and reset date.
 * 
 * @example
 * ```tsx
 * <BudgetExhaustedModal
 *   open={showBudgetModal}
 *   onOpenChange={setShowBudgetModal}
 *   details={{ usedUsd: 100, budgetUsd: 100, resetDate: '2026-03-01', percentUsed: 100 }}
 * />
 * ```
 */
export function BudgetExhaustedModal({
  open,
  onOpenChange,
  details,
}: BudgetExhaustedModalProps) {
  // Format currency
  const formatUsd = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  // Format reset date
  const formatResetDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    })
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-2 text-red-600">
            <Ban className="h-5 w-5" />
            <AlertDialogTitle>Monthly Budget Exhausted</AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-4 pt-2">
              <p>
                You've used {details ? formatUsd(details.usedUsd) : '$0.00'} of your{' '}
                {details ? formatUsd(details.budgetUsd) : '$0.00'} monthly budget.
              </p>
              
              {details && (
                <BudgetProgress
                  percentUsed={details.percentUsed}
                  status="critical"
                  size="md"
                />
              )}
              
              {details?.resetDate && (
                <p>
                  Your budget will reset on <strong>{formatResetDate(details.resetDate)}</strong>.
                </p>
              )}
              
              <p className="text-muted-foreground">
                Contact your administrator if you need additional budget.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction>Close</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
